import React from 'react';
import styled from 'styled-components';
import { merge } from 'lodash';
import Modal from 'react-responsive-modal';
import type { SwiperOptions } from 'swiper';
import SwiperCore, {
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
} from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.css';
import type { PaginationOptions } from 'swiper/types';

import ImageComponent from './ImageComponent';
import EmbeddedVideo from './EmbeddedVideo';
import { ArrowComponent } from './ArrowComponent';

import {
  defaultStyles,
  mobileStyles,
  mobileVideoModalStyles,
  navigation,
} from '../styles';
import { pushPlayGalleryVideoAnalytics } from '../analytics';
import type { PopupSwiperProps } from '../types';

SwiperCore.use([
  Navigation,
  Scrollbar,
  Mousewheel,
  Pagination,
  Thumbs,
  Keyboard,
]);

export const PopupSwiper = ({
  opened,
  video,
  images,
  title,
  hotelId,
  swiperGalleryPopup,
  realIndexSlide,
  isMobile,
  closePopup,
  setSwiperGalleryPopup,
  changeSlide,
}: PopupSwiperProps) => {
  const paramsPopup: SwiperOptions = {
    pagination: {
      type: 'fraction',
    } as PaginationOptions,
    keyboard: {
      enabled: true,
    },
    allowTouchMove: !isMobile,
    navigation,
    initialSlide: realIndexSlide,
    lazyPreloadPrevNext: 6,
    loop: true,
  };

  const showVideo = !!video;
  const isMobileVideo = isMobile && showVideo;

  const handleVideoPlay = () => {
    if (!hotelId) return;
    pushPlayGalleryVideoAnalytics(hotelId, title);
  };

  return (
    <>
      {/** TODO: @see https://tracker.yandex.ru/LT-39577 */}
      {/** @ts-ignore */}
      <Modal
        showCloseIcon
        onClose={() => closePopup(swiperGalleryPopup?.activeIndex)}
        open={opened}
        styles={merge(
          isMobile ? mobileStyles : defaultStyles,
          isMobileVideo ? mobileVideoModalStyles : {},
        )}
      >
        <StyledSwiperPopup
          {...paramsPopup}
          onSwiper={setSwiperGalleryPopup}
          onSlideChange={() => changeSlide?.()}
          className="swiper-container"
          $isMobileVideo={isMobileVideo}
          $isMobile={isMobile}
        >
          <div className="swiper-wrapper">
            {images.map((image, index) => (
              <SwiperSlide key={`slide-${image}`}>
                {showVideo && index === 0 ? (
                  <EmbeddedVideo
                    video={video}
                    title={title}
                    isMobile={isMobile}
                    onPlay={handleVideoPlay}
                  />
                ) : (
                  <ImageComponent src={image} title={title} isLazyLoading />
                )}
              </SwiperSlide>
            ))}
          </div>
          <ArrowComponent isMobile={isMobile} />
        </StyledSwiperPopup>
      </Modal>
    </>
  );
};

const defaultPopupStyles = `
  width: 1200px;
  height: 80vh;
`;

const mobilePopupStyles = `
  width: 100%;
  height: 100%;
  .swiper-button-prev {
    left: 0;
    svg {
      width: 20px;
    }
  }
  .swiper-button-next {
    right: 0;
    svg {
      width: 20px;
    }
  }
`;

const mobileVideoPopupStyles = `
  .swiper-button-next, .swiper-button-prev {
    display: none;
  }

  height: 100%;
`;

const StyledSwiperPopup = styled(Swiper)<{
  $isMobileVideo: boolean;
  $isMobile: boolean;
}>`
  .swiper-pagination {
    color: #fff;
  }
  img {
    height: 100%;
  }

  ${({ $isMobileVideo }) =>
    $isMobileVideo ? mobileVideoPopupStyles : defaultPopupStyles}
  ${({ $isMobile }) => ($isMobile ? mobilePopupStyles : defaultPopupStyles)}
`;
